import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  InputNumber,
  Modal,
  Segmented,
  Select,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  EyeOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import ReactMarkdown from 'react-markdown';
import PageContainer from '@/components/PageContainer';
import {
  fetchBacktestReadyMonths,
  runBacktestComparison,
} from '@/api/mediumTermBacktest';
import type {
  AvailabilityRecord,
  BacktestComparisonRequest,
  BacktestComparisonResult,
  BacktestRole,
} from '@/api/mediumTermBacktest';
import PortfolioLedger from './medium-term-backtest/PortfolioLedger';
import StrategyActionLine from './medium-term-backtest/StrategyActionLine';
import StrategyExplanationPanel from './medium-term-backtest/StrategyExplanationPanel';
import type { PortfolioKey } from './medium-term-backtest/viewModel';
import helpContent from './MediumTermBacktestHelp.md?raw';
import './MediumTermBacktestPage.css';

const formatMoney = (value: number) =>
  new Intl.NumberFormat('zh-CN', { maximumFractionDigits: 0 }).format(value);

const formatRate = (value: number | null) =>
  value == null ? '-' : `${(value * 100).toFixed(1)}%`;

const toRatio = (percent: number) => Number((percent / 100).toFixed(4));

function NumberField({
  ariaLabel,
  value,
  onChange,
  unit,
  min,
  max,
  step,
}: {
  ariaLabel: string;
  value: number;
  onChange: (value: number | null) => void;
  unit: string;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <div className="mtb-number-field">
      <InputNumber
        aria-label={ariaLabel}
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        step={step}
      />
      <span>{unit}</span>
    </div>
  );
}

export default function MediumTermBacktestPage() {
  const [months, setMonths] = useState<string[]>([]);
  const [month, setMonth] = useState('');
  const [role, setRole] = useState<BacktestRole>('buyer');
  const [buyerLowerPct, setBuyerLowerPct] = useState(44.4);
  const [buyerUpperPct, setBuyerUpperPct] = useState(74.4);
  const [buyerBoundsConfirmed, setBuyerBoundsConfirmed] = useState(false);
  const [sellerFlatPct, setSellerFlatPct] = useState(30);
  const [entryThreshold, setEntryThreshold] = useState(15);
  const [halfResponseSpread, setHalfResponseSpread] = useState(15);
  const [maxAdjustmentPct, setMaxAdjustmentPct] = useState(50);
  const [result, setResult] = useState<BacktestComparisonResult | null>(null);
  const [running, setRunning] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioKey>('MOMENTUM');
  const requestVersion = useRef(0);

  const clearResult = useCallback(() => {
    requestVersion.current += 1;
    setResult(null);
    setRunning(false);
    setSelectedPortfolio('MOMENTUM');
  }, []);

  useEffect(() => {
    let active = true;
    fetchBacktestReadyMonths()
      .then((values) => {
        if (!active) return;
        setMonths(values);
        setMonth((current) => current || values[0] || '');
      })
      .catch((error) => {
        console.error(error);
        message.error('可回测月份加载失败');
      });
    return () => { active = false; };
  }, []);

  const configError = useMemo(() => {
    if (!month) return '没有可回测月份';
    if (halfResponseSpread <= 0) return '半响应价差必须大于 0';
    if (maxAdjustmentPct < 0 || maxAdjustmentPct > 100) return '最大调仓比例必须在 0%-100%';
    if (role === 'buyer') {
      if (buyerLowerPct < 0 || buyerUpperPct > 100 || buyerLowerPct > buyerUpperPct) {
        return '买方持仓边界必须满足 0% ≤ 下界 ≤ 上界 ≤ 100%';
      }
      if (!buyerBoundsConfirmed) return '请确认所选月份的买方持仓边界';
    }
    return null;
  }, [month, role, buyerLowerPct, buyerUpperPct, buyerBoundsConfirmed, halfResponseSpread, maxAdjustmentPct]);

  const handleRoleChange = (value: string | number) => {
    setRole(value as BacktestRole);
    setBuyerBoundsConfirmed(false);
    clearResult();
  };

  const handleMonthChange = (value: string) => {
    setMonth(value);
    setBuyerBoundsConfirmed(false);
    clearResult();
  };

  const handleRun = async () => {
    if (configError) {
      message.warning(configError);
      return;
    }
    clearResult();
    const version = requestVersion.current;
    setRunning(true);
    const request: BacktestComparisonRequest = {
      trade_month: month,
      role,
      analysis_mode: 'SINGLE_RUN',
      entry_threshold: entryThreshold,
      half_response_spread: halfResponseSpread,
      max_adjustment_ratio: toRatio(maxAdjustmentPct),
      buyer_lower_bound: role === 'buyer' ? toRatio(buyerLowerPct) : undefined,
      buyer_upper_bound: role === 'buyer' ? toRatio(buyerUpperPct) : undefined,
      buyer_bounds_confirmed: role === 'buyer' ? buyerBoundsConfirmed : false,
      seller_flat_ratio: toRatio(sellerFlatPct),
    };
    try {
      const response = await runBacktestComparison(request);
      if (version !== requestVersion.current) return;
      setSelectedPortfolio(
        response.run_status === 'COMPLETED' && response.comparison_leader
          ? response.comparison_leader
          : 'MOMENTUM',
      );
      setResult(response);
      message.success('整月策略比较完成');
    } catch (error) {
      if (version !== requestVersion.current) return;
      message.error(error instanceof Error ? error.message : '回测失败');
    } finally {
      if (version === requestVersion.current) setRunning(false);
    }
  };

  const availabilityColumns: ColumnsType<AvailabilityRecord> = [
    { title: '窗口', dataIndex: 'window_index', width: 70, render: (value) => `W${value}` },
    { title: '交易日', dataIndex: 'trade_date', width: 110 },
    { title: '标的日', dataIndex: 'target_date', width: 110 },
    { title: '提前天数', dataIndex: 'lead_days', width: 85, render: (value) => `D+${value}` },
    {
      title: '分区', dataIndex: 'trade_zone', width: 100,
      render: (value) => value === 'ACTIVE_NEAR'
        ? '主动区'
        : role === 'buyer' ? '合规区' : '远期非主动区',
    },
    {
      title: '状态', dataIndex: 'availability_status', width: 130,
      render: (value) => value === 'EXECUTABLE'
        ? <Tag color="green">可执行</Tag>
        : <Tag>无已报告价格</Tag>,
    },
    {
      title: '窗口价', dataIndex: 'execution_price', align: 'right',
      render: (value) => value == null ? '-' : `¥${value.toFixed(2)}`,
    },
    {
      title: '来源状态', dataIndex: 'source_value_state', width: 92,
      render: (value) => value === 'PRESENT' ? '已报告' : '空白',
    },
    {
      title: '来源引用', dataIndex: 'source_reference', width: 230,
      render: (value) => <span className="mtb-source-reference">{value}</span>,
    },
  ];

  const weightsColumns: ColumnsType<{ hour: number; weight: number }> = [
    { title: '小时', dataIndex: 'hour', render: (value) => `${value}:00` },
    { title: '全省结算权重', dataIndex: 'weight', align: 'right', render: (value) => `${(value * 100).toFixed(4)}%` },
  ];

  return (
    <PageContainer noCard>
      <main className="mtb-page">
        <header className="mtb-header">
          <div>
            <div className="mtb-header__title-row">
              <h1>中长期回测</h1>
              <Tag color="cyan">确定性回放</Tag>
              <Tag>整月双策略</Tag>
            </div>
            <div className="mtb-header__meta">
              <span>山东市场</span>
              <span>月度标准化 10,000 MWh</span>
              <span>D+4 至 D+10 主动交易</span>
            </div>
          </div>
          <Tooltip title="查看回测口径">
            <Button
              aria-label="查看回测口径"
              icon={<QuestionCircleOutlined />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </header>

        <div className="mtb-workbench">
          <aside className="mtb-controls" aria-label="回测参数">
            <section className="mtb-control-section">
              <div className="mtb-section-label">交易身份</div>
              <Segmented
                block
                value={role}
                onChange={handleRoleChange}
                options={[
                  { label: '买方', value: 'buyer' },
                  { label: '卖方', value: 'seller' },
                ]}
              />
            </section>

            <section className="mtb-control-section">
              <label className="mtb-field-label" htmlFor="mtb-month">交易月份</label>
              <Select
                id="mtb-month"
                aria-label="交易月份"
                value={month || undefined}
                options={months.map((value) => ({ value, label: value }))}
                onChange={handleMonthChange}
                placeholder="暂无可回测月份"
              />
            </section>

            {role === 'buyer' ? (
              <section className="mtb-control-section">
                <div className="mtb-section-label">买方月度红线</div>
                <div className="mtb-field-grid">
                  <label>
                    <span>持仓下界</span>
                    <NumberField
                      ariaLabel="买方持仓下界"
                      min={0}
                      max={100}
                      step={0.1}
                      value={buyerLowerPct}
                      onChange={(value) => { setBuyerLowerPct(value ?? 0); setBuyerBoundsConfirmed(false); clearResult(); }}
                      unit="%"
                    />
                  </label>
                  <label>
                    <span>持仓上界</span>
                    <NumberField
                      ariaLabel="买方持仓上界"
                      min={0}
                      max={100}
                      step={0.1}
                      value={buyerUpperPct}
                      onChange={(value) => { setBuyerUpperPct(value ?? 0); setBuyerBoundsConfirmed(false); clearResult(); }}
                      unit="%"
                    />
                  </label>
                </div>
                <Checkbox
                  checked={buyerBoundsConfirmed}
                  onChange={(event) => { setBuyerBoundsConfirmed(event.target.checked); clearResult(); }}
                >
                  确认本月持仓边界
                </Checkbox>
                <div className="mtb-bound-mwh">
                  {formatMoney(buyerLowerPct * 100)} - {formatMoney(buyerUpperPct * 100)} MWh
                </div>
              </section>
            ) : (
              <section className="mtb-control-section">
                <label className="mtb-field-label">
                  卖方平铺基准比例
                  <NumberField
                    ariaLabel="卖方平铺基准比例"
                    min={0}
                    max={100}
                    step={1}
                    value={sellerFlatPct}
                    onChange={(value) => { setSellerFlatPct(value ?? 30); clearResult(); }}
                    unit="%"
                  />
                </label>
                <div className="mtb-bound-mwh">空仓 0 MWh / 平铺 {formatMoney(sellerFlatPct * 100)} MWh</div>
              </section>
            )}

            <section className="mtb-control-section">
              <div className="mtb-section-label">信号调仓</div>
              <label className="mtb-field-label">
                开仓阈值
                <NumberField
                  ariaLabel="开仓阈值"
                  min={0}
                  value={entryThreshold}
                  onChange={(value) => { setEntryThreshold(value ?? 0); clearResult(); }}
                  unit="元/MWh"
                />
              </label>
              <label className="mtb-field-label">
                半响应价差
                <NumberField
                  ariaLabel="半响应价差"
                  min={0.01}
                  value={halfResponseSpread}
                  onChange={(value) => { setHalfResponseSpread(value ?? 15); clearResult(); }}
                  unit="元/MWh"
                />
              </label>
              <label className="mtb-field-label">
                最大调仓比例
                <NumberField
                  ariaLabel="最大调仓比例"
                  min={0}
                  max={100}
                  value={maxAdjustmentPct}
                  onChange={(value) => { setMaxAdjustmentPct(value ?? 50); clearResult(); }}
                  unit="%"
                />
              </label>
            </section>

            <section className="mtb-contract-grid" aria-label="固定回测合同">
              <div><span>月度电量</span><strong>10,000 MWh</strong></div>
              <div><span>单日仓位</span><strong>0% - 100%</strong></div>
              <div><span>策略锁定</span><strong>整月</strong></div>
              <div><span>成交模型</span><strong>记录价全成</strong></div>
            </section>

            {configError && configError !== '请确认所选月份的买方持仓边界' ? (
              <div className="mtb-config-error">{configError}</div>
            ) : null}
            <Button
              type="primary"
              size="large"
              block
              icon={<PlayCircleOutlined />}
              loading={running}
              disabled={Boolean(configError)}
              onClick={handleRun}
            >
              运行整月对比
            </Button>
          </aside>

          <section className="mtb-results" aria-live="polite">
            {running ? (
              <div className="mtb-empty"><Spin size="large" /><strong>正在回放整月窗口</strong></div>
            ) : !result ? (
              <div className="mtb-empty"><span className="mtb-empty__mark">M / R</span><strong>暂无回测结果</strong></div>
            ) : (
              <>
                <div className="mtb-result-head">
                  <div>
                    <span className="mtb-eyebrow">{result.trade_month} / {result.role === 'buyer' ? '买方' : '卖方'}</span>
                    <h2>动量与反转整月对比</h2>
                  </div>
                  <div className="mtb-result-head__status">
                    <Tag color={result.run_status === 'COMPLETED' ? 'green' : 'red'}>
                      {result.run_status === 'COMPLETED'
                        ? '回放完成'
                        : result.role === 'buyer' ? '合规不可行' : '回测不可行'}
                    </Tag>
                    <Tag color="gold">探索性</Tag>
                  </div>
                </div>

                {result.run_status === 'INFEASIBLE' ? (
                  <Alert
                    type="error"
                    showIcon
                    message={result.role === 'buyer' ? '合规不可行' : '回测不可行'}
                    description={result.role === 'buyer'
                      ? '至少一个买方组合无法在当前窗口的真实可执行远端标的上恢复持仓下界。'
                      : '至少一个卖方组合无法在当前交易边界内完成回放。'}
                  />
                ) : null}

                <div className="mtb-quality-strip">
                  <span className={result.availability_summary.overall.coverage_rate === 1 ? 'is-complete' : 'is-warning'}>
                    价格数据完整 {formatRate(result.availability_summary.overall.coverage_rate)}（主动区 {result.availability_summary.ACTIVE_NEAR.executable_count}/{result.availability_summary.ACTIVE_NEAR.expected_count}，{result.role === 'buyer' ? '合规区' : '远期区'} {result.availability_summary.COMPLIANCE_FAR.executable_count}/{result.availability_summary.COMPLIANCE_FAR.expected_count}）
                  </span>
                  <span>10,000 MWh 标准化月度电量</span>
                  <span>全省统一月度加权结算价</span>
                  <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setWeightsOpen(true)}>查看 24 小时权重</Button>
                </div>

                <StrategyExplanationPanel result={result} />
                <StrategyActionLine
                  result={result}
                  portfolio={selectedPortfolio}
                  onPortfolioChange={setSelectedPortfolio}
                />

                <Tabs
                  className="mtb-tabs"
                  defaultActiveKey="ledger"
                  items={[
                    {
                      key: 'ledger',
                      label: '逐笔账本',
                      children: (
                        <PortfolioLedger
                          result={result}
                          portfolio={selectedPortfolio}
                          onPortfolioChange={setSelectedPortfolio}
                        />
                      ),
                    },
                    {
                      key: 'audit',
                      label: '数据与运行审计',
                      children: (
                        <div className="mtb-audit-pane" data-testid="data-run-audit">
                          <div className="mtb-audit-summary">
                            <span>全月 {result.availability_summary.overall.executable_count}/{result.availability_summary.overall.expected_count}</span>
                            <span>主动区 D+4-D+10 {result.availability_summary.ACTIVE_NEAR.executable_count}/{result.availability_summary.ACTIVE_NEAR.expected_count}</span>
                            <span>{result.role === 'buyer' ? '合规区 D+11 起' : '远期非主动区'} {result.availability_summary.COMPLIANCE_FAR.executable_count}/{result.availability_summary.COMPLIANCE_FAR.expected_count}</span>
                          </div>
                          <div className="mtb-window-coverage">
                            <strong>逐窗口覆盖</strong>
                            <div>
                              {result.availability_summary.by_window.map((window) => (
                                <span key={`${window.window_index}-${window.execution_order}`}>
                                  <b>W{window.window_index} · 执行序 {window.execution_order}</b>
                                  {window.executable_count}/{window.expected_count} · {formatRate(window.coverage_rate)}
                                </span>
                              ))}
                              {!result.availability_summary.by_window.length ? <span>暂无窗口汇总</span> : null}
                            </div>
                          </div>
                          <Table
                            rowKey={(row) => `${row.window_index}-${row.target_date}`}
                            dataSource={result.availability_records}
                            columns={availabilityColumns}
                            size="small"
                            pagination={{ pageSize: 20, showSizeChanger: false }}
                            scroll={{ x: 1080 }}
                          />
                          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }} className="mtb-run-audit">
                            <Descriptions.Item label="策略锁定">整月</Descriptions.Item>
                            <Descriptions.Item label="证据等级">{result.evidence_level === 'EXPLORATORY' ? '探索性' : '样本外'}</Descriptions.Item>
                            <Descriptions.Item label="权重月份">{result.settlement_weight_month}</Descriptions.Item>
                            <Descriptions.Item label="权重合计">{result.settlement_weight_sum.toFixed(6)}</Descriptions.Item>
                            <Descriptions.Item label="权重来源" span={2}>{result.settlement_weight_source_reference}</Descriptions.Item>
                            <Descriptions.Item label="成交假设" span={2}>历史记录价 / 硬约束内全部成交 / 零滑点与手续费</Descriptions.Item>
                          </Descriptions>
                        </div>
                      ),
                    },
                  ]}
                />
              </>
            )}
          </section>
        </div>
      </main>

      <Modal
        title={`${result?.settlement_weight_month ?? month} 全省结算权重`}
        open={weightsOpen}
        onCancel={() => setWeightsOpen(false)}
        footer={<Button onClick={() => setWeightsOpen(false)}>关闭</Button>}
        width={520}
      >
        <Table
          rowKey="hour"
          dataSource={result?.settlement_weight_values ?? []}
          columns={weightsColumns}
          pagination={false}
          size="small"
          scroll={{ y: 420 }}
        />
      </Modal>

      <Modal
        title="中长期回测口径"
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        footer={<Button type="primary" onClick={() => setHelpOpen(false)}>关闭</Button>}
        width={760}
      >
        <div className="mtb-help"><ReactMarkdown>{helpContent}</ReactMarkdown></div>
      </Modal>
    </PageContainer>
  );
}
